import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useProgressStore } from "../../app/stores/progressStore";
import { lessonPath } from "../../app/routes";
import { getUnits, type CourseId } from "../engine/lessonRegistry";
import { LessonRenderer } from "./LessonRenderer";
import styles from "./LessonRoute.module.css";

function parseIndex(value: string | undefined, prefix: string): number | null {
  if (!value) return null;
  const match = value.match(new RegExp("^" + prefix + "(\\d+)$"));
  return match ? Number(match[1]) : null;
}

interface LessonRouteProps {
  course: CourseId;
}

export function LessonRoute({ course }: LessonRouteProps) {
  const params = useParams();
  const navigate = useNavigate();
  const setLocation = useProgressStore((s) => s.setLocation);
  const finishLesson = useProgressStore((s) => s.finishLesson);

  const unit = parseIndex(params.unit, "unit");
  const lesson = parseIndex(params.lesson, "lesson");

  useEffect(() => {
    if (unit !== null && lesson !== null) setLocation(course, unit, lesson);
  }, [course, unit, lesson, setLocation]);

  const config = useMemo(() => {
    if (unit === null || lesson === null) return null;
    const targetUnit = getUnits(course)[unit - 1];
    const factory = targetUnit?.lessons[lesson - 1];
    return factory ? factory() : null;
  }, [course, unit, lesson]);

  const handleFinished = useCallback(() => {
    const next = finishLesson();
    navigate(lessonPath(next.course, next.unit, next.lesson));
  }, [finishLesson, navigate]);

  if (unit === null || lesson === null || !config) {
    return <p className={styles.notFound}>Lesson not found.</p>;
  }

  return (
    <LessonRenderer
      key={course + "-" + unit + "-" + lesson}
      config={config}
      onFinished={handleFinished}
    />
  );
}
